import React, { useState } from "react";
import { toast } from "sonner";
import { Gift, ArrowRight, Heart } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const PRESETS = [50, 100, 150, 250];
const field = "w-full bg-[#1a140e] border border-[rgba(211,218,221,0.16)] rounded-xl px-4 py-3 text-[#f6ecd4] placeholder:text-[#7f9a9a] outline-none focus:border-[#2fd0c6] focus:bg-[#241a11] focus:shadow-[0_0_0_3px_rgba(47,208,198,0.18)] transition";

export default function GiftCardPurchase() {
  const [amount, setAmount] = useState(100);
  const [custom, setCustom] = useState("");
  const [buyerName, setBuyerName] = useState("");
  const [buyerEmail, setBuyerEmail] = useState("");
  const [sendToRecipient, setSendToRecipient] = useState(false);
  const [recipientName, setRecipientName] = useState("");
  const [recipientEmail, setRecipientEmail] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const finalAmount = custom ? Number(custom) : amount;

  const buy = async () => {
    if (!buyerName.trim() || !buyerEmail.trim()) { toast.error("Please enter your name and email."); return; }
    if (!finalAmount || finalAmount < 10 || finalAmount > 2000) { toast.error("Choose an amount between $10 and $2000."); return; }
    if (sendToRecipient && (!recipientName.trim() || !recipientEmail.trim())) { toast.error("Please enter the recipient's name and email."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/giftcards/checkout", {
        amount: finalAmount, buyer_name: buyerName, buyer_email: buyerEmail,
        recipient_name: recipientName, recipient_email: recipientEmail,
        message, send_to_recipient: sendToRecipient, origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Something went wrong. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 card p-6 sm:p-8 space-y-6" data-testid="giftcard-form">
        <div>
          <label className="text-sm font-semibold text-[#f6ecd4] mb-3 block">Choose an Amount</label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {PRESETS.map((p) => (
              <button key={p} onClick={() => { setAmount(p); setCustom(""); }} data-testid={`giftcard-amount-${p}`}
                className={`py-3 rounded-xl font-mono-arcade text-xl font-bold border-2 transition ${!custom && amount === p ? "border-[#ff5147] bg-[#ff5147]/15 text-[#ff5147]" : "border-[rgba(211,218,221,0.16)] text-[#a9bdbd] hover:border-[#2fd0c6]"}`}>${p}</button>
            ))}
          </div>
          <div className="mt-3">
            <label className="text-xs text-[#8fa5a5]">Or enter a custom amount ($10–$2000)</label>
            <input type="number" min={10} max={2000} className={`${field} mt-1`} placeholder="Custom amount" value={custom} onChange={(e) => setCustom(e.target.value)} data-testid="giftcard-custom-amount" />
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div><label className="text-sm font-semibold text-[#f6ecd4]">Your Name</label><input className={`${field} mt-2`} value={buyerName} onChange={(e) => setBuyerName(e.target.value)} data-testid="giftcard-buyer-name" /></div>
          <div><label className="text-sm font-semibold text-[#f6ecd4]">Your Email</label><input type="email" className={`${field} mt-2`} value={buyerEmail} onChange={(e) => setBuyerEmail(e.target.value)} data-testid="giftcard-buyer-email" /></div>
        </div>

        <label className="flex items-start gap-3 cursor-pointer">
          <input type="checkbox" checked={sendToRecipient} onChange={(e) => setSendToRecipient(e.target.checked)} className="mt-1 accent-[#2fd0c6] h-4 w-4" data-testid="giftcard-send-toggle" />
          <span className="text-sm text-[#a9bdbd]"><span className="font-semibold text-[#f6ecd4] flex items-center gap-2"><Heart size={14} className="text-[#ff5147]" /> Send this as a gift to someone else</span>We'll email the code directly to them with your message.</span>
        </label>

        {sendToRecipient && (
          <div className="rounded-2xl p-5 border-2 border-[rgba(47,208,198,0.35)] bg-[#241a11] space-y-4" data-testid="giftcard-recipient-block">
            <div className="grid sm:grid-cols-2 gap-4">
              <div><label className="text-sm font-semibold text-[#f6ecd4]">Recipient Name</label><input className={`${field} mt-2`} value={recipientName} onChange={(e) => setRecipientName(e.target.value)} data-testid="giftcard-recipient-name" /></div>
              <div><label className="text-sm font-semibold text-[#f6ecd4]">Recipient Email</label><input type="email" className={`${field} mt-2`} value={recipientEmail} onChange={(e) => setRecipientEmail(e.target.value)} data-testid="giftcard-recipient-email" /></div>
            </div>
            <div><label className="text-sm font-semibold text-[#f6ecd4]">Personal Message (optional)</label><textarea rows={3} className={`${field} mt-2`} placeholder="Happy Birthday! Have a blast at Keys." value={message} onChange={(e) => setMessage(e.target.value)} data-testid="giftcard-message" /></div>
          </div>
        )}
      </div>

      <div className="lg:sticky lg:top-28 h-fit">
        <div className="card p-6" data-testid="giftcard-summary">
          <div className="flex items-center gap-2 mb-4"><Gift className="text-[#ff5147]" /><h3 className="font-display font-bold text-lg text-[#f6ecd4]">Gift Card</h3></div>
          <div className="rounded-xl border-2 border-dashed border-[rgba(47,208,198,0.5)] p-6 text-center mb-4">
            <div className="font-mono-arcade text-5xl font-extrabold text-[#ff5147]" data-testid="giftcard-total">${finalAmount || 0}</div>
            <div className="text-xs text-[#8fa5a5] mt-2">Keys Event Center</div>
          </div>
          <p className="text-xs text-[#8fa5a5] mb-4">A unique code will be emailed after payment{sendToRecipient ? " to your recipient" : ""}. Redeemable toward any reservation. No expiration.</p>
          <button onClick={buy} disabled={loading} className="btn-primary w-full py-4 flex items-center justify-center gap-2 disabled:opacity-60" data-testid="giftcard-buy-btn">
            {loading ? "Processing…" : <>Buy Gift Card <ArrowRight size={16} /></>}
          </button>
        </div>
      </div>
    </div>
  );
}
